import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { consentsColumns } from '../../utils/definitions.util'
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental'
import { ConsentService } from '../../services/entity-services/consent.service'

const DEFAULT_FILTERS = {
  name: '',
}

@Component({
  selector: 'app-consents',
  templateUrl: 'consents.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ConsentsComponent extends BaseListing implements OnInit {
  private consentService = inject(ConsentService)

  listingColumns = signal(consentsColumns)
  sortProp = signal('name')
  sortDir = signal<'asc' | 'desc'>('asc')

  // injectInfiniteQuery is initialized before ngOnInit, so restore the filter here
  // to ensure the first request honors the saved view instead of overwriting it.
  filters = signal(this.loadViewSettings('consents')?.filters || structuredClone(DEFAULT_FILTERS))

  totalItems = computed(() => this.queryConsents.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  readonly queryFilters = computed(() => {
    const f: Record<string, any> = {}
    const value = this.filters()
    if (value.name?.length) f['name'] = value.name
    return f
  })

  readonly queryConsents = injectInfiniteQuery(() => ({
    queryKey: ['consents', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      // Persist current view settings before fetching, per tags.component.ts pattern
      return await this.consentService
        .getConsents(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          {
            id: null,
            name: null,
            externalId: null,
            created: null,
            updated: null,
          }
        )
        .then(result => {
          this.saveViewSettings('consents', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result => {
      return Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined
    },
  }))

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryConsents.data()
    })
  }

  ngOnInit() {
    const view = this.loadViewSettings('consents')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('consents')
  }

  onNameFilterChange(name: string) {
    this.filters.update(v => ({ ...v, name: name ?? '' }))
  }

  clearFilters(): void {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryConsents.refetch()
  }
}
